import { Alert } from '../../alerts/models/Alert';
import { Site, SiteId } from '../../site-detail/models/Site';
import { getClientName } from '../../site-owner/utils/owner.util';
import { Favorite } from '../models/favorite';
import { FavoriteSite } from '../models/favorite-site';

export function buildFavoriteSites(
    favorites: readonly Favorite[],
    sites: Record<SiteId, Site>,
    alerts: readonly Alert[],
): FavoriteSite[] {
    return favorites
        .filter(({ resourceOID }) => sites[resourceOID])
        .map((favorite) => {
            const site = sites[favorite.resourceOID];
            const siteAlerts = alerts.filter((alert) => alert.id_site === favorite.resourceOID);

            const favoriteSite: FavoriteSite = {
                siteId: site.id,
                favoriteId: favorite.favoriteOID,
                gatewayId: site.gatewayId,
                nbSetupGateways: site.setupGateways?.length ?? 0,
                clientName: getClientName(site, false),
            };

            const alert = getAlertWithHighSeverity(siteAlerts);
            if (alert) {
                favoriteSite.alert = { severity: alert.severity, date: alert.date };
            }

            return favoriteSite;
        })
        .sort((siteA, siteB) => compareAlerts(siteA.alert, siteB.alert));
}

function getAlertWithHighSeverity(alerts: readonly Alert[]): Alert | undefined {
    return alerts.reduce<Alert | undefined>(
        (highest, current) => (getMostRelevantAlert(highest, current) === current ? current : highest),
        undefined,
    );
}

function getMostRelevantAlert(
    alertA: Partial<Alert> | undefined,
    alertB: Partial<Alert> | undefined,
): Partial<Alert> | undefined {
    if (alertA == null || alertB == null) return alertA ?? alertB;
    if ((alertA.severity ?? 0) > (alertB.severity ?? 0)) return alertA;
    if (alertA.severity === alertB.severity && (alertA.date ?? 0) > (alertB.date ?? 0)) return alertA;

    return alertB;
}

function compareAlerts(alertA: Partial<Alert> | undefined, alertB: Partial<Alert> | undefined): number {
    if (alertA == null || alertB == null) {
        if (alertA == null && alertB == null) return 0;
        return alertA == null ? 1 : -1;
    }

    const severityDifference = (alertB.severity ?? 0) - (alertA.severity ?? 0);
    if (severityDifference !== 0) return severityDifference;

    return (alertB.date ?? 0) - (alertA.date ?? 0);
}
